/**
 * One fixed asset: what it is, when it was put to use, what it cost and how it is written off -
 * with the AfA year by year shown while it is typed (lib/at/afa.ts), so the person sees what the
 * income statement will carry. A purchase invoice can be its source (`document`); the ledger then
 * books that invoice on the asset account instead of an expense.
 */
import { zodResolver } from "@hookform/resolvers/zod";
import { Trash2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useDeleteAsset, useSaveAsset } from "@/hooks/use-assets";
import { afaProblem, afaSchedule, gwgLimit, isGwg, type AssetInput } from "@/lib/at/afa";
import type { Asset, AssetFields } from "@/lib/api/assets";
import { ASSET_ACCOUNT } from "@/lib/ledger/money";
import { useWorkspace } from "@/lib/session/workspace-context";
import { formatMoney, parseDecimal } from "@/lib/utils/format";

const day = (value: string | undefined) => (value ?? "").slice(0, 10);
const decimal = (value: number | undefined) =>
  value === undefined || value === null || value === 0 ? "" : String(value).replace(".", ",");

/** Which input a rule the asset breaks belongs to. */
const PROBLEM_FIELD = {
  no_date: "inServiceDate",
  no_cost: "cost",
  life_too_short: "usefulLifeYears",
  car_life: "usefulLifeYears",
  degressive_rate: "degressiveRate",
  degressive_car: "method",
  disposal_before_service: "disposalDate",
} as const;

export function AssetDialog({
  open,
  onOpenChange,
  asset,
  prefill,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The asset to edit; a new one when absent. */
  asset?: Asset | null;
  /** Taken from a purchase invoice. */
  prefill?: Partial<AssetFields>;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        {open && (
          <AssetForm
            key={asset?.id ?? "new"}
            asset={asset ?? null}
            prefill={prefill}
            onDone={() => onOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function AssetForm({
  asset,
  prefill,
  onDone,
}: {
  asset: Asset | null;
  prefill?: Partial<AssetFields>;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const { workspace, canWrite } = useWorkspace();
  const save = useSaveAsset(workspace?.id ?? "");
  const remove = useDeleteAsset();

  const schema = z.object({
    name: z.string().trim().min(1, t("assets.nameRequired")).max(200),
    type: z.enum(["general", "car"]),
    inServiceDate: z.string().min(10, t("assets.problem.no_date")),
    cost: z.string().refine((value) => (parseDecimal(value) ?? 0) > 0, t("assets.problem.no_cost")),
    usefulLifeYears: z.string(),
    method: z.enum(["linear", "degressive"]),
    degressiveRate: z.string(),
    privateUsePercent: z.string().refine((value) => {
      const share = parseDecimal(value) ?? 0;
      return share >= 0 && share <= 100;
    }, t("assets.privateInvalid")),
    disposalDate: z.string(),
    account: z.string().max(20),
    notes: z.string().max(1000),
  });
  type Values = z.infer<typeof schema>;
  const source = { ...asset, ...prefill };
  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: source.name ?? "",
      type: source.type ?? "general",
      inServiceDate: day(source.inServiceDate),
      cost: decimal(source.cost),
      usefulLifeYears: source.usefulLifeYears ? String(source.usefulLifeYears) : "",
      method: source.method ?? "linear",
      degressiveRate: source.degressiveRate ? String(source.degressiveRate) : "30",
      privateUsePercent: decimal(source.privateUsePercent),
      disposalDate: day(source.disposalDate),
      account: source.account ?? "",
      notes: source.notes ?? "",
    },
  });

  const toInput = (values: Values): AssetInput => ({
    type: values.type,
    inServiceDate: values.inServiceDate,
    cost: parseDecimal(values.cost) ?? 0,
    usefulLifeYears: Number(values.usefulLifeYears) || 0,
    method: values.method,
    degressiveRate: values.method === "degressive" ? (parseDecimal(values.degressiveRate) ?? 0) : 0,
    privateUsePercent: parseDecimal(values.privateUsePercent) ?? 0,
    disposalDate: values.disposalDate || undefined,
  });

  const watched = form.watch();
  const input = toInput(watched);
  const gwg = isGwg(input);
  const schedule = afaSchedule(input);
  const type = watched.type;
  const method = watched.method;

  async function submit(values: Values) {
    const parsed = toInput(values);
    const problem = afaProblem(parsed);
    if (problem) {
      form.setError(PROBLEM_FIELD[problem], { message: t(`assets.problem.${problem}`) });
      return;
    }
    try {
      await save.mutateAsync({
        id: asset?.id,
        fields: {
          name: values.name.trim(),
          type: parsed.type,
          inServiceDate: parsed.inServiceDate,
          cost: parsed.cost,
          usefulLifeYears: parsed.usefulLifeYears,
          method: parsed.method,
          degressiveRate: parsed.degressiveRate ?? 0,
          privateUsePercent: parsed.privateUsePercent ?? 0,
          disposalDate: parsed.disposalDate ?? "",
          account: values.account.trim(),
          document: asset?.document ?? prefill?.document ?? "",
          notes: values.notes.trim(),
        },
      });
      toast.success(asset ? t("assets.saved") : t("assets.created"));
      onDone();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("assets.failed"));
    }
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>{asset ? asset.name : t("assets.new")}</DialogTitle>
        <DialogDescription>{t("assets.dialogHint")}</DialogDescription>
      </DialogHeader>
      <Form {...form}>
        <form
          id="asset-form"
          className="grid gap-4"
          data-testid="asset-form"
          onSubmit={form.handleSubmit(submit)}
        >
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("assets.name")}</FormLabel>
                <FormControl>
                  <Input disabled={!canWrite} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="grid gap-4 sm:grid-cols-3">
            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("assets.type")}</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange} disabled={!canWrite}>
                    <FormControl>
                      <SelectTrigger data-testid="asset-type">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="general">{t("assets.types.general")}</SelectItem>
                      <SelectItem value="car">{t("assets.types.car")}</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="inServiceDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("assets.inServiceDate")}</FormLabel>
                  <FormControl>
                    <Input type="date" disabled={!canWrite} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="cost"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("assets.cost")}</FormLabel>
                  <FormControl>
                    <Input inputMode="decimal" className="num" disabled={!canWrite} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <p className="-mt-2 text-xs text-muted-foreground">
            {type === "car" ? t("assets.costHintCar") : t("assets.costHint")}
          </p>

          {gwg ? (
            <p className="rounded-md bg-muted px-3 py-2 text-sm" data-testid="asset-gwg">
              {t("assets.gwg", {
                limit: formatMoney(gwgLimit(watched.inServiceDate || "2026-01-01")),
              })}
            </p>
          ) : (
            <div className="grid gap-4 sm:grid-cols-3">
              <FormField
                control={form.control}
                name="usefulLifeYears"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("assets.usefulLife")}</FormLabel>
                    <FormControl>
                      <Input inputMode="numeric" className="num" disabled={!canWrite} {...field} />
                    </FormControl>
                    <FormDescription>
                      {type === "car" ? t("assets.lifeHintCar") : t("assets.lifeHint")}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="method"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("assets.method")}</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange} disabled={!canWrite}>
                      <FormControl>
                        <SelectTrigger data-testid="asset-method">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="linear">{t("assets.methods.linear")}</SelectItem>
                        <SelectItem value="degressive">{t("assets.methods.degressive")}</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {method === "degressive" && (
                <FormField
                  control={form.control}
                  name="degressiveRate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("assets.degressiveRate")}</FormLabel>
                      <FormControl>
                        <Input
                          inputMode="decimal"
                          className="num"
                          disabled={!canWrite}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-3">
            <FormField
              control={form.control}
              name="privateUsePercent"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("assets.privateUse")}</FormLabel>
                  <FormControl>
                    <Input
                      inputMode="decimal"
                      className="num"
                      placeholder="0"
                      disabled={!canWrite}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="disposalDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("assets.disposalDate")}</FormLabel>
                  <FormControl>
                    <Input type="date" disabled={!canWrite} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="account"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("assets.account")}</FormLabel>
                  <FormControl>
                    <Input
                      className="num"
                      placeholder={ASSET_ACCOUNT}
                      disabled={!canWrite}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <FormField
            control={form.control}
            name="notes"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("assets.notes")}</FormLabel>
                <FormControl>
                  <Textarea rows={2} disabled={!canWrite} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </form>
      </Form>

      {schedule.length > 0 && (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm" data-testid="asset-schedule">
            <thead className="bg-muted/50 text-xs text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left font-medium">{t("assets.year")}</th>
                <th className="px-3 py-2 text-right font-medium">{t("assets.afa")}</th>
                <th className="px-3 py-2 text-right font-medium">{t("assets.deductible")}</th>
                <th className="px-3 py-2 text-right font-medium">{t("assets.bookValue")}</th>
              </tr>
            </thead>
            <tbody>
              {schedule.map((row) => (
                <tr key={row.year} className="border-t" data-testid="asset-schedule-row">
                  <td className="px-3 py-1.5 num">
                    {row.year}
                    {row.half && (
                      <span className="ml-1 text-xs text-muted-foreground">{t("assets.half")}</span>
                    )}
                  </td>
                  <td className="px-3 py-1.5 text-right num">
                    {formatMoney((row.depreciation + row.residual) / 100)}
                  </td>
                  <td className="px-3 py-1.5 text-right num">
                    {formatMoney(row.deductible / 100)}
                  </td>
                  <td className="px-3 py-1.5 text-right num">
                    {formatMoney(row.bookValueEnd / 100)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {canWrite && (
        <DialogFooter className="gap-2 sm:justify-between">
          {asset ? (
            <Button
              variant="ghost"
              data-testid="delete-asset"
              disabled={remove.isPending}
              onClick={async () => {
                await remove.mutateAsync(asset.id);
                toast.success(t("assets.deleted"));
                onDone();
              }}
            >
              <Trash2 /> {t("common.delete")}
            </Button>
          ) : (
            <span />
          )}
          <Button
            type="submit"
            form="asset-form"
            disabled={save.isPending}
            data-testid="save-asset"
          >
            {t("common.save")}
          </Button>
        </DialogFooter>
      )}
    </>
  );
}
